import { ReactElement, ReactNode } from 'react';
import {
  alpha,
  backdropClasses,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  ThemeProvider,
} from '@mui/material';
import { DialogClasses } from '@mui/material/Dialog/dialogClasses';
import { makeStyles } from 'tss-react/mui';

import { CloseIcon } from 'modules/icons';
import { createMainTheme } from 'modules/themes';

interface Props extends MuiDialogProps {
  header?: ReactNode | string;
  onClose?: () => void;
}

const EMPTY_CLASSES: Partial<DialogClasses> = {};

const lightTheme = createMainTheme();

export function Dialog({
  onClose,
  header,
  children,
  ...props
}: Props): ReactElement {
  const { classes, cx } = useStyles();
  const propsClasses = props?.classes || EMPTY_CLASSES;

  return (
    <ThemeProvider theme={lightTheme}>
      <MuiDialog
        {...props}
        classes={{
          ...propsClasses,
          paper: cx(classes.paper, propsClasses?.paper),
        }}
        className={cx(classes.root, props.className)}
        onClose={onClose}
      >
        <div className={classes.content}>
          {header && <div className={classes.title}>{header}</div>}

          <div className={classes.button} onClick={onClose}>
            <CloseIcon />
          </div>

          {children}
        </div>
      </MuiDialog>
    </ThemeProvider>
  );
}

export const useStyles = makeStyles()(theme => ({
  root: {
    [`.${backdropClasses.root}`]: {
      background: `linear-gradient(236.45deg, ${alpha(theme.palette.primary.contrastText, 0.92)} 70.48%, #16152A 92.99%)`,
    },
  },

  paper: {
    width: '100%',
    maxWidth: 600,
    padding: theme.spacing(7, 10, 10, 10),
    margin: theme.spacing(5),
    background: `linear-gradient(110.01deg, ${theme.palette.background.default} 8.7%, ${theme.palette.primary.contrastText} 40.24%)`,

    [theme.breakpoints.down('sm')]: {
      maxWidth: 'none',
      padding: theme.spacing(7, 4, 11),
    },
  },

  content: {
    zIndex: 1,
    width: '100%',
  },

  title: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: theme.spacing(8),

    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(7.5),
    },
  },

  button: {
    position: 'absolute',
    cursor: 'pointer',
    top: theme.spacing(7),
    right: theme.spacing(8),
    color: theme.palette.grey[600],

    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(4),
    },

    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));
